/**
 * SEO composables for consistent canonical URLs and meta tags
 */

/**
 * Generate a canonical URL for the current page
 * Removes query parameters and ensures consistent format
 */
export function useCanonicalUrl(path?: string) {
  const config = useRuntimeConfig()
  const route = useRoute()
  
  // Use provided path or current route path (without query params)
  const cleanPath = path || route.path
  
  // Ensure baseURL doesn't have trailing slash and path has leading slash
  const baseUrl = (config.public.baseURL || '').replace(/\/$/, '')
  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`
  
  return `${baseUrl}${normalizedPath}`
}

/**
 * Set SEO meta tags with brand-aware defaults
 */
export function useBrandSeoMeta(options: {
  title: string
  description: string
  ogImage?: string
  canonical?: string
  noindex?: boolean
}) {
  const config = useRuntimeConfig()
  const { $siteConfig } = useNuxtApp()
  const route = useRoute()
  
  const brand = $siteConfig?.value?.brand || config.public.brand || 'Tripening'
  const canonicalUrl = options.canonical || useCanonicalUrl()
  
  // Default OG image fallback
  const ogImage = options.ogImage || `${config.public.baseURL}/images/og-default-${brand.toLowerCase()}.jpg`
  
  useHead({
    title: options.title,
    link: [
      { rel: 'canonical', href: canonicalUrl }
    ],
    meta: [
      { name: 'description', content: options.description },
      { property: 'og:title', content: options.title },
      { property: 'og:description', content: options.description },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:image', content: ogImage },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: brand },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: options.title },
      { name: 'twitter:description', content: options.description },
      { name: 'twitter:image', content: ogImage },
      ...(options.noindex ? [{ name: 'robots', content: 'noindex, nofollow' }] : [])
    ]
  })
}

/**
 * Generate FAQ structured data (JSON-LD) for FAQ sections
 */
export function useFaqSchema(faqItems: Array<{ title: string; text: string }>) {
  if (!faqItems || faqItems.length === 0) return null
  
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqItems.map(item => ({
      '@type': 'Question',
      'name': item.title,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.text
      }
    }))
  }
}

/**
 * Generate BreadcrumbList structured data.
 * Pure function — does NOT call composables, so it's safe inside useJsonld() callbacks.
 */
export function useBreadcrumbSchema(breadcrumbs: Array<{ label: string; url?: string }>, baseUrl: string) {
  const cleanBaseUrl = (baseUrl || '').replace(/\/$/, '')

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.label,
      ...(item.url && {
        'item': item.url.startsWith('http') ? item.url : `${cleanBaseUrl}${item.url}`
      })
    }))
  }
}

/**
 * Add hreflang alternate language link tags for SEO
 * Helps search engines understand language variants of the page
 */
export function useHreflangTags(path?: string) {
  const config = useRuntimeConfig()
  const route = useRoute()
  const { locale } = useI18n()

  const locales = ['en', 'es', 'de', 'it', 'pt', 'ru', 'ja']
  const baseUrl = (config.public.baseURL || '').replace(/\/$/, '')

  // Use provided path or current route path (strip locale prefix if present)
  let cleanPath = path || route.path

  // Remove existing locale prefix from path if present
  for (const loc of locales) {
    if (cleanPath.startsWith(`/${loc}/`) || cleanPath === `/${loc}`) {
      cleanPath = cleanPath.slice(loc.length + 1) || '/'
      break
    }
  }

  // Ensure path starts with /
  if (!cleanPath.startsWith('/')) {
    cleanPath = `/${cleanPath}`
  }

  const links = locales.map(loc => ({
    rel: 'alternate',
    hreflang: loc,
    href: loc === 'en' ? `${baseUrl}${cleanPath}` : `${baseUrl}/${loc}${cleanPath}`
  }))

  // Add x-default pointing to English version
  links.push({
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${baseUrl}${cleanPath}`
  })

  useHead({ link: links })
}
